import api from '../httpClient';

export interface InvoiceLineItem {
  description: string;
  qty: number;
  unitPrice: number;
  total: number;
}

export interface InvoicePartySnapshot {
  name?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  [key: string]: unknown;
}

export interface Invoice {
  id: string;
  invoiceNo: string;
  nutritionistId: string;
  clientId: string;
  nutritionistSnapshot?: InvoicePartySnapshot | null;
  clientSnapshot?: InvoicePartySnapshot | null;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  taxPct: number;
  taxAmount: number;
  discount: number;
  totalDue: number;
  currency: string;
  status: string;
  issueDate: string;
  dueDate: string;
  subscriptionId?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  sentAt?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  transactionRef?: string | null;
  discardedAt?: string | null;
  notes?: string | null;
  service?: string;
  amount?: number | string | null;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown; // Allow other properties
}

export type InvoiceDateBasis = 'invoiceDate' | 'paidDate';

export interface InvoicesResponse {
  invoices: Invoice[];
  summary: {
    totalInvoicedThisMonth?: number;
    collected?: {
      amount?: number;
      invoiceCount?: number;
    };
    overdueCount?: number;
    totalBilled: number;
    totalPaid: number;
    totalDue?: number;
    totalOutstanding: number;
    dateBasis?: InvoiceDateBasis;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  [key: string]: unknown;
}

export interface GetClientInvoicesParams {
  clientId?: string;
  status?: 'paid' | 'due' | 'archived' | string;
  sort?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  /** Server-side filter (e.g. client name, invoice number). */
  search?: string;
  fromDate?: string;
  toDate?: string;
  /** Nutritionist billing currency (e.g. INR, USD). */
  currency?: string;
  dateBasis?: InvoiceDateBasis;
}

export const getClientInvoices = async (
  clientIdOrParams?: string | GetClientInvoicesParams
): Promise<InvoicesResponse> => {
  const params: GetClientInvoicesParams =
    typeof clientIdOrParams === 'string'
      ? { clientId: clientIdOrParams }
      : (clientIdOrParams || {});

  const query = new URLSearchParams();
  if (params.clientId) query.set('clientId', params.clientId);
  if (params.status) query.set('status', params.status);
  if (params.sort) query.set('sort', params.sort);
  if (typeof params.page === 'number') query.set('page', String(params.page));
  if (typeof params.limit === 'number') query.set('limit', String(params.limit));
  if (params.search?.trim()) query.set('search', params.search.trim());
  if (params.fromDate) query.set('fromDate', params.fromDate);
  if (params.toDate) query.set('toDate', params.toDate);
  if (params.currency?.trim()) query.set('currency', params.currency.trim().toUpperCase());
  if (params.dateBasis) query.set('dateBasis', params.dateBasis);

  const qs = query.toString();
  const url = qs ? `/client-invoices?${qs}` : '/client-invoices';
  const response = await api.get(url);
  return response.data?.data || response.data;
};

export interface CreateInvoicePayload {
  invoiceNo: string;
  nutritionistId: string;
  clientId: string;
  lineItems: {
    description: string;
    qty: number;
    unitPrice: number;
  }[];
  taxPct: number;
  discount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  sendEmail: boolean;
  notes: string | null;
}

export interface InvoiceContextPlan {
  id: string;
  name: string;
  totalAmount: number | null;
  nutritionistCountry: string;
  nutritionistCurrency: string;
}

export interface NutritionistInvoiceContext {
  paymentDueDays: number;
  showLogoOnInvoice: boolean;
  vatRate: number;
  currency: string;
  clinicLogoUrl?: string;
  nutritionistCountry: string;
  nutritionistCurrency: string;
  plans: InvoiceContextPlan[];
}

export interface GeneratedInvoiceId {
  invoiceNo: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const requireStringField = (record: Record<string, unknown>, key: string): string => {
  const value = record[key];
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  throw new Error(`Invoice context is missing ${key}`);
};

const requireNumberField = (record: Record<string, unknown>, key: string): number => {
  const value = record[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(parsed)) return parsed;
  }
  throw new Error(`Invoice context is missing ${key}`);
};

const readOptionalNumberField = (record: Record<string, unknown>, ...keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
};

const readOptionalBooleanField = (
  record: Record<string, unknown>,
  key: string,
  defaultValue: boolean
): boolean => {
  const value = record[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return defaultValue;
};

const readOptionalStringField = (record: Record<string, unknown>, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const CLINIC_LOGO_KEYS = ['clinicLogoUrl', 'clinicLogo', 'practiceLogoUrl', 'practiceLogo', 'logoUrl'] as const;
const CLINIC_LOGO_NESTED_KEYS = ['practiceInfo', 'practice', 'profile', 'nutritionist', 'user'] as const;

const readOptionalClinicLogoUrl = (value: unknown, depth = 0): string | undefined => {
  const record = asRecord(value);
  const direct = readOptionalStringField(record, ...CLINIC_LOGO_KEYS);
  if (direct || depth >= 2) {
    return direct;
  }

  for (const key of CLINIC_LOGO_NESTED_KEYS) {
    const nested = readOptionalClinicLogoUrl(record[key], depth + 1);
    if (nested) {
      return nested;
    }
  }

  return undefined;
};

const normalizeInvoiceContextPlan = (
  value: unknown,
  defaults: Pick<NutritionistInvoiceContext, 'nutritionistCountry' | 'nutritionistCurrency' | 'currency'>
): InvoiceContextPlan | null => {
  const record = asRecord(value);
  const id = readOptionalStringField(record, 'id', '_id', 'planId');
  const name = readOptionalStringField(record, 'name', 'title', 'planName', 'programmeName');
  const totalAmount = readOptionalNumberField(
    record,
    'totalAmount',
    'totalPrice',
    'priceAmount',
    'price',
    'rate',
    'amount',
    'total',
  );

  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    totalAmount: totalAmount !== undefined && totalAmount >= 0 ? totalAmount : null,
    nutritionistCountry: readOptionalStringField(record, 'nutritionistCountry') || defaults.nutritionistCountry,
    nutritionistCurrency: readOptionalStringField(record, 'nutritionistCurrency') || defaults.nutritionistCurrency || defaults.currency,
  };
};

const normalizeNutritionistInvoiceContext = (value: unknown): NutritionistInvoiceContext => {
  const record = asRecord(value);
  if (!Array.isArray(record.plans)) {
    throw new Error('Invoice context is missing plans');
  }

  const nutritionistCountry = requireStringField(record, 'nutritionistCountry');
  const nutritionistCurrency = requireStringField(record, 'nutritionistCurrency');
  const currency = requireStringField(record, 'currency');

  const contextDefaults = {
    nutritionistCountry,
    nutritionistCurrency,
    currency,
  };

  return {
    paymentDueDays: requireNumberField(record, 'paymentDueDays'),
    showLogoOnInvoice: readOptionalBooleanField(record, 'showLogoOnInvoice', true),
    vatRate: requireNumberField(record, 'vatRate'),
    currency,
    clinicLogoUrl: readOptionalClinicLogoUrl(record),
    nutritionistCountry,
    nutritionistCurrency,
    plans: record.plans
      .map((plan) => normalizeInvoiceContextPlan(plan, contextDefaults))
      .filter((plan): plan is InvoiceContextPlan => plan !== null),
  };
};

const normalizeGeneratedInvoiceId = (value: unknown): GeneratedInvoiceId => {
  const record = asRecord(value);
  const invoiceNo =
    typeof record.invoiceNo === 'string' && record.invoiceNo.trim()
      ? record.invoiceNo.trim()
      : '';

  if (!invoiceNo) {
    throw new Error('Generated invoice id response is missing invoiceNo');
  }

  return { invoiceNo };
};

export const getNutritionistInvoiceContext = async (): Promise<NutritionistInvoiceContext> => {
  const response = await api.get('/nutritionists/invoice-context');
  return normalizeNutritionistInvoiceContext(response.data?.data || response.data);
};

export const getGeneratedClientInvoiceId = async (): Promise<GeneratedInvoiceId> => {
  const response = await api.get('/client-invoices/generate-id');
  return normalizeGeneratedInvoiceId(response.data?.data);
};

export const createClientInvoice = async (payload: CreateInvoicePayload): Promise<Invoice> => {
  const response = await api.post('/client-invoices', payload);
  return response.data?.data || response.data;
};

export const getClientInvoice = async (invoiceId: string): Promise<Invoice> => {
  const response = await api.get(`/client-invoices/${invoiceId}`);
  return response.data?.data || response.data;
};

const normalizeInvoiceClinicLogoDataUrl = (value: unknown): string | null => {
  const data = asRecord(value);
  return readOptionalStringField(data, 'clinicLogoDataUrl') || null;
};

export const getClientInvoiceClinicLogoDataUrl = async (invoiceId: string): Promise<string | null> => {
  const response = await api.get(`/client-invoices/${invoiceId}/clinic-logo-data-url`);
  return normalizeInvoiceClinicLogoDataUrl(response.data?.data || response.data);
};

export interface UpdateInvoiceStatusPayload {
  status: 'sent' | 'paid' | 'discarded' | 'archived' | 'overdue' | string;
  paidAt?: string;
  paymentMethod?: string;
  transactionRef?: string;
}

export const updateInvoiceStatus = async (
  invoiceId: string,
  payload: UpdateInvoiceStatusPayload
): Promise<Invoice> => {
  const response = await api.patch(`/client-invoices/${invoiceId}/status`, payload);
  return response.data?.data || response.data;
};

/** Restores an archived invoice (returns it to the active pipeline, e.g. Due). */
export const unarchiveClientInvoice = async (invoiceId: string): Promise<Invoice> => {
  const response = await api.patch(`/client-invoices/${invoiceId}/unarchive`);
  return response.data?.data || response.data;
};

export const resendInvoiceEmail = async (invoiceId: string): Promise<unknown> => {
  const response = await api.post(`/client-invoices/${invoiceId}/resend-email`);
  return response.data;
};

export const deleteClientInvoice = async (invoiceId: string): Promise<void> => {
  await api.delete(`/client-invoices/${invoiceId}`);
};
